import React, { Suspense, lazy } from 'react';
import PropTypes from 'prop-types';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { Toaster } from './components/ui/sonner';
import { ThemeProvider } from './lib/theme-provider';
import AuthProvider from './providers/AuthProvider';
import BorrowerAuthProvider, { useBorrowerAuth } from './providers/BorrowerAuthProvider';
import { ProtectedRoute, PublicOnlyRoute } from './components/auth/RouteGuards';
import AppShell from './components/layout/AppShell';
import AppRoutes from './routes';
import { Loader2 } from 'lucide-react';

// Public pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import FeaturesPage from './pages/FeaturesPage';
import CreditLockout from './pages/CreditLockout';
import ForceChangePassword from './pages/ForceChangePassword';
import DiagnosticLogs from './pages/DiagnosticLogs';

// Public signing page (lazy)
const MockSignPage = lazy(() => import('./pages/MockSignPage'));

// Borrower Portal pages (lazy)
const PortalLoginPage = lazy(() => import('./pages/portal/PortalLoginPage'));
const PortalDashboard = lazy(() => import('./pages/portal/PortalDashboard'));
const PortalLoansPage = lazy(() => import('./pages/portal/PortalLoansPage'));
const PortalLoanDetail = lazy(() => import('./pages/portal/PortalLoanDetail'));
const BorrowerLayout = lazy(() => import('./components/portal/BorrowerLayout'));

function PortalSuspense({ children }) {
  return <Suspense fallback={<div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}>{children}</Suspense>;
}

PortalSuspense.propTypes = {
  children: PropTypes.node.isRequired,
};

function BorrowerProtected({ children }) {
  const { isAuthenticated } = useBorrowerAuth();
  if (!isAuthenticated) return <Navigate to="/portal/login" replace />;
  return children;
}

BorrowerProtected.propTypes = {
  children: PropTypes.node.isRequired,
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <BorrowerAuthProvider>
          <ThemeProvider>
            <Routes>
              {/* Lender Portal */}
              <Route path="/login" element={<LoginPage />} />
              {process.env.REACT_APP_REGISTRATION_ENABLED === 'true' && (
                <Route path="/register" element={<RegisterPage />} />
              )}
              <Route path="/credit-lockout" element={<CreditLockout />} />
              <Route path="/force-change-password" element={<ForceChangePassword />} />
              <Route path="/diagnostics" element={<DiagnosticLogs />} />

              {/* Mock Signing (public — no auth) */}
              <Route path="/esign/mock-sign/:mockId" element={<PortalSuspense><MockSignPage /></PortalSuspense>} />

              {/* Borrower Portal */}
              <Route path="/portal/login" element={<PortalSuspense><PortalLoginPage /></PortalSuspense>} />
              <Route path="/portal" element={<BorrowerProtected><PortalSuspense><BorrowerLayout><PortalDashboard /></BorrowerLayout></PortalSuspense></BorrowerProtected>} />
              <Route path="/portal/loans" element={<BorrowerProtected><PortalSuspense><BorrowerLayout><PortalLoansPage /></BorrowerLayout></PortalSuspense></BorrowerProtected>} />
              <Route path="/portal/loans/:loanId" element={<BorrowerProtected><PortalSuspense><BorrowerLayout><PortalLoanDetail /></BorrowerLayout></PortalSuspense></BorrowerProtected>} />

              {/* Lender Portal (protected) */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <Routes>
                        {AppRoutes()}
                      </Routes>
                    </AppShell>
                  </ProtectedRoute>
                }
              />
            </Routes>
            <Toaster position="top-right" />
          </ThemeProvider>
        </BorrowerAuthProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
